import { Table } from "./Table";
import { SpreadsheetCell } from "../s25-spreadsheet/spreadsheet.service";
import { Nullable } from "../../pojo/Util";

const comparator = Intl.Collator().compare;

export class S25TableUtil {
    public static emulateInfiniteScroll(config: SimulationConfig): Table.InfiniteScroll {
        let filteredRows: Table.Row[] = config.rows;
        config.pageSize ??= 20;

        // Return pertinent slice of data
        const dataSource = async (query: Table.Query) => {
            if (config.dataSource && (!config.rows || query.forceRefresh)) {
                config.rows = await config.dataSource(query);
            }

            // Filter data
            const filterFunc = config.filterFunction || S25TableUtil.filterRows;
            filteredRows = config.rows.slice().filter((row) => filterFunc(query.filterValue, row, config.columns));

            // Sort data
            if (query.sortColumn.id) {
                const sortedColumn = config.columns.find((column) => column.id === query.sortColumn.id);
                const sortFunc = config.sortFunction || S25TableUtil.sortRows;
                filteredRows.sort((a: Table.Row, b: Table.Row) => sortFunc(a, b, sortedColumn, query.sortColumn.order));
            }

            // Get slice of data
            const slice = filteredRows.slice(query.page * config.pageSize, (query.page + 1) * config.pageSize);
            return {
                rows: slice,
                totalRows: filteredRows.length,
                cacheId: query.cacheId,
            };
        };

        // Check whether there is more data to load
        const hasMorePages = (currentPage: number) => filteredRows.length > (currentPage + 1) * config.pageSize;

        return {
            type: "infinite scroll",
            dataSource,
            columns: config.columns,
            hasMorePages,
        };
    }

    public static sortRows(rowA: Table.Row, rowB: Table.Row, column: Table.Column, order: "asc" | "desc") {
        if (order === "asc") [rowA, rowB] = [rowB, rowA]; // Swap if reverse order
        const aVal = S25TableUtil.getSortValue(rowA, column);
        const bVal = S25TableUtil.getSortValue(rowB, column);
        return comparator(aVal, bVal);
    }

    public static getSortValue(row: Table.Row, column: Table.Column): string {
        const cell = row.cells[column.id];
        const col = column.cells;
        const sortProp = cell?.sortProp || col?.sortProp;
        const sortInstance = cell?.instance || col?.instance;
        const val =
            cell?.sortValue ??
            col?.sortValue ??
            cell?.text ??
            col?.text ??
            cell?.textValue ??
            col?.textValue ??
            (sortProp ? (sortInstance as any)?.[sortProp] : null) ??
            "";
        return String(val);
    }

    public static filterRows(filterValue: string, row: Table.Row, columns: Table.Column[]): boolean {
        // Filter based on row name
        filterValue = filterValue.toLowerCase();
        if (row.name && String(row.name).toLowerCase().includes(filterValue)) return true;
        // Filter based on text cells and textValue (for component cells)
        for (const column of columns) {
            const text = S25TableUtil.getFilterValue(row, column);
            if (text.toLowerCase().includes(filterValue)) return true;
        }
    }

    public static getFilterValue(row: Table.Row, column: Table.Column): string {
        const cell = row.cells[column.id];
        const col = column.cells;
        const val =
            cell?.filterValue ?? col?.filterValue ?? cell?.text ?? col?.text ?? cell?.textValue ?? col?.textValue ?? "";
        return String(val).toLowerCase();
    }

    // Get a cell's final config based on column defaults and cell values
    public static getConfig(defaults: Table.Cell, overrides: Table.Cell) {
        // If both are defined, combine
        if (overrides && defaults) return { ...defaults, ...overrides };
        // Return whichever is defined
        return overrides ?? defaults;
    }

    // Get the spreadsheet value of a cell
    public static getCellSpreadsheetValue(
        cell: Nullable<Table.Cell>,
    ): ReturnType<SpreadsheetCell["getSpreadsheetCellValue"]> {
        if (!cell) return undefined;
        const component = cell.instance;
        if (
            component &&
            typeof component === "object" &&
            "getSpreadsheetCellValue" in component &&
            typeof component.getSpreadsheetCellValue === "function"
        ) {
            return component.getSpreadsheetCellValue.call(component);
        }
        if (cell.text) return cell.text;
        if (cell.textValue) return cell.textValue;
        return undefined;
    }
}

type SimulationConfig = {
    columns: Table.Column[];
    rows?: Table.Row[];
    dataSource?: (query: Table.UnpaginatedQuery) => Promise<Table.Row[]>; // Used on refresh and if rows is undefined
    pageSize?: number;
    sortFunction?: (a: Table.Row, b: Table.Row, column: Table.Column, order: "asc" | "desc") => number;
    filterFunction?: (value: string, row: Table.Row, columns: Table.Column[]) => boolean;
};
